import { useCallback, useEffect, useRef, useState } from 'react';
import type { StoreInstallationView, StoreSetup } from '../../api';
import { erpApi } from '../../api';
import { ConfirmDialog } from '../ui';
import { useStoreCopy } from './storeCopy';

/** How often the guide asks whether the store has checked in. */
const POLL_MS = 5000;
/** After this long without the store, the guide says what usually blocks it. */
const SLOW_MS = 3 * 60 * 1000;

type Phase = 'waiting' | 'slow' | 'connected';

/**
 * StoreConnectGuide walks a person through connecting one store (CO-82):
 * install the extension, copy the one connection code, paste it in the
 * store's admin (Magento or WordPress),
 * and then watch the store check in. The code carries the store's signing
 * key and is shown only here, so the guide never disappears by accident.
 *
 * "Connected" means the store has signed a request with the key in this
 * code: a new store has been seen, or a store given a new code has stopped
 * using its previous one (previous_key_active turns false).
 */
export function StoreConnectGuide({
  projectId,
  setup,
  onConnected,
  onClose,
}: {
  projectId: string;
  setup: StoreSetup;
  onConnected: () => void;
  onClose: () => void;
}) {
  const store = setup.installation;
  const words = useStoreCopy();
  const reconnecting = Boolean(store.last_seen_at);
  const [phase, setPhase] = useState<Phase>('waiting');
  const [reported, setReported] = useState<StoreInstallationView | null>(null);
  const [copied, setCopied] = useState<'yes' | 'manual' | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const codeRef = useRef<HTMLElement>(null);
  const connectedRef = useRef(onConnected);
  connectedRef.current = onConnected;

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
    headingRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [store.id]);

  // Poll while waiting, and at once when the person comes back from the
  // store's admin tab. Stop as soon as the store is connected.
  useEffect(() => {
    if (phase === 'connected') return;
    let cancelled = false;
    const check = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const { stores } = await erpApi.projectStores(projectId);
        const current = stores.find((entry) => entry.id === store.id);
        if (!cancelled && current?.last_seen_at && !current.previous_key_active) {
          setReported(current);
          setPhase('connected');
          connectedRef.current();
        }
      } catch {
        // A failed poll is retried on the next tick; the guide keeps waiting.
      }
    };
    const timer = window.setInterval(() => void check(), POLL_MS);
    const onVisible = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [phase, projectId, store.id]);

  useEffect(() => {
    if (phase !== 'waiting') return;
    const timer = window.setTimeout(() => setPhase('slow'), SLOW_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  // Leaving the page loses the code for good; the browser asks first.
  useEffect(() => {
    if (phase === 'connected') return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(setup.connection_code);
      setCopied('yes');
    } catch {
      // No clipboard access (an insecure origin, a denied permission):
      // select the code so a keyboard copy takes all of it.
      const node = codeRef.current;
      const selection = window.getSelection();
      if (node && selection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setCopied('manual');
    }
  }, [setup.connection_code]);

  const baseUrl = reported?.reported_base_urls[0];

  return (
    <section className="erp-connect-guide" aria-labelledby="erp-connect-guide-title" data-testid="erp-store-setup">
      <div className="erp-connect-guide__head">
        <h3 id="erp-connect-guide-title" className="erp-connect-guide__title" ref={headingRef} tabIndex={-1}>
          {reconnecting ? `Reconnect ${store.name}` : `Connect ${store.name}`}
        </h3>
        {phase === 'connected' ? null : (
          <button type="button" className="button button--ghost button--small" onClick={() => setConfirmClose(true)}>
            Close
          </button>
        )}
      </div>

      {phase === 'connected' ? null : (
        <ol className="erp-connect-steps">
          {reconnecting ? null : (
            <li>
              <strong>{words.installTitle}</strong>
              <span className="field__help">{words.installHelp}</span>
              {words.downloadable ? (
                <a
                  className="button button--secondary button--small erp-connect-download"
                  href={erpApi.storePluginUrl(projectId)}
                  download
                  data-testid="erp-store-plugin-download"
                >
                  Download the plugin
                </a>
              ) : null}
            </li>
          )}
          <li>
            <strong>Copy the connection code</strong>
            <div className="erp-connect-code">
              <code ref={codeRef} translate="no" data-testid="erp-store-code">
                {setup.connection_code}
              </code>
              <button type="button" className="button button--secondary button--small" onClick={() => void copy()}>
                {copied === 'yes' ? 'Copied' : 'Copy code'}
              </button>
              <span className="visually-hidden" role="status">
                {copied === 'yes' ? 'Connection code copied.' : ''}
              </span>
            </div>
            <span className="field__help" role={copied === 'manual' ? 'alert' : undefined}>
              {copied === 'manual'
                ? 'The browser did not allow copying. The code is selected: press Ctrl+C (⌘C on a Mac).'
                : 'Shown only now. It holds the store’s signing key, so paste it only into this store.'}
            </span>
          </li>
          <li>
            <strong>
              Paste it in {words.adminShort} and click {words.saveButton}
            </strong>
            <span className="field__help">
              {words.connectionPath}, in the Connection code field.
              {reconnecting ? ' Until then the store keeps using its previous code.' : ''}
            </span>
          </li>
        </ol>
      )}

      <div
        className={`erp-connect-status erp-connect-status--${phase}`}
        role="status"
        data-testid="erp-store-connect-status"
      >
        <span
          className={`live-dot ${phase === 'connected' ? 'live-dot--green' : phase === 'slow' ? 'live-dot--amber' : 'live-dot--blue'}`}
          aria-hidden="true"
        />
        <div className="erp-connect-status__text">
          {phase === 'connected' ? (
            <>
              <strong>Connected.</strong> {store.name} checked in
              {baseUrl ? ` from ${baseUrl}` : ''}.
            </>
          ) : phase === 'waiting' ? (
            <>
              <strong>Waiting for {store.name} to check in…</strong> It checks within a minute of{' '}
              {words.saveButton}, or at once when you click <em>{words.checkButton}</em> in {words.adminShort}.
            </>
          ) : (
            <>
              <strong>{store.name} has not checked in yet.</strong> In {words.adminShort}, click{' '}
              <em>{words.checkButton}</em>: it says what is wrong. Usually one of these:
              <ul>
                <li>The code was pasted but {words.saveButton} was not clicked.</li>
                <li>The store’s server cannot reach {setup.url}.</li>
                <li>The server’s clock is more than 5 minutes off.</li>
                <li>{words.schedulerProblem}</li>
              </ul>
            </>
          )}
        </div>
      </div>

      {phase === 'connected' ? (
        <button type="button" className="button button--primary" onClick={onClose}>
          Done
        </button>
      ) : null}

      <ConfirmDialog
        open={confirmClose}
        title="Close without connecting?"
        description={`${store.name} has not used this code yet, and it cannot be shown again. If you have not saved it in ${words.adminShort}, close this and issue a new connection code from the store’s card.`}
        confirmLabel="Close"
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => {
          setConfirmClose(false);
          onClose();
        }}
      />
    </section>
  );
}
